import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { createExamCategory, updateExamCategory, clearError, clearSuccess } from "../../redux/slices/examCategorySlice";
import { toast } from "react-toastify";
import { FaRegTrashAlt } from "react-icons/fa";

const ExamCategoryModal = ({ show, handleClose, editId = null }) => {
  const dispatch = useDispatch();
  const { examCategories, isLoading } = useSelector((state) => state.examCategories);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    image_url: "",
    status: "Active",
    image_url: "",
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [validated, setValidated] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (show) {
      if (editId) {
        const category = examCategories.find((c) => c.id === editId);
        if (category) {
          setFormData({
            name: category.name || "",
            description: category.description || "",
            image_url: category.image_url || "",
            status: category.status || "Active",
          });
        }
      } else {
        setFormData({
          name: "",
          description: "",
          image_url: "",
          status: "Active",
        });
      }
      setImageFile(null);
      setValidated(false);
    }
  }, [editId, examCategories, show]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let finalValue = value;
    if (name === "name") {
      finalValue = value.replace(/[^a-zA-Z\s]/g, "");
    }
    setFormData((prev) => ({ ...prev, [name]: finalValue }));
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        e.target.value = "";
        return;
      }

      // Mandatory Dimension Validation: 2200 x 2000 Px
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.onload = () => {
        if (img.width !== 2200 || img.height !== 2000) {
          toast.error(
            `Image dimensions must be exactly 2200 x 2000 pixels. (Detected: ${img.width}x${img.height})`
          );
          setImageFile(null);
          e.target.value = "";
        } else {
          setImageFile(file);
        }
        URL.revokeObjectURL(objectUrl);
      };
      img.onerror = () => {
        toast.error("Failed to load image for validation");
        URL.revokeObjectURL(objectUrl);
        e.target.value = "";
      };
      img.src = objectUrl;
    } else {
      setImageFile(null);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setFormData((prev) => ({ ...prev, image_url: "" }));
  };

  const handleImageChange = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }
  };

  const validateForm = () => {
    const errors = {};
    const catName = formData.name.trim();

    if (!catName) {
      errors.name = "Category name is required";
    } else if (!/^[a-zA-Z\s]+$/.test(catName)) {
      errors.name = "Category name should only contain letters and spaces";
    }

    if (!imageFile && !formData.image_url) {
      errors.image = "Image is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix validation errors");
      return;
    }

    try {
      if (editId) {
        await dispatch(updateExamCategory({ id: editId, data: formData, imageFile })).unwrap();
        toast.success("Exam category updated successfully");
      } else {
        await dispatch(createExamCategory({ data: formData, imageFile })).unwrap();
        toast.success("Exam category created successfully");
      }
      handleClose();
    } catch (error) {
      toast.error(error || "An error occurred");
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton className="bg-dark text-white">
        <Modal.Title>{editId ? "Edit Exam Category" : "Add Exam Category"}</Modal.Title>
      </Modal.Header>
      <Form noValidate onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Category Name <span className="text-danger">*</span></Form.Label>
            <Form.Control
              required
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g. UPSC, SSC, Banking"
              disabled={isLoading}
            />
            <Form.Control.Feedback type="invalid">{validationErrors.name}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter description"
              disabled={isLoading}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Category Image <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              isInvalid={!!validationErrors.image}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.image}
            </Form.Control.Feedback>
            {(imageFile || formData.image_url) && (
              <div className="mt-2 text-center">
                <img
                  src={imageFile ? URL.createObjectURL(imageFile) : formData.image_url}
                  alt="Preview"
                  style={{ maxWidth: "100%", maxHeight: "150px", objectFit: "contain", borderRadius: "4px", border: "1px solid #dee2e6" }}
                />
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <Form.Select name="status" value={formData.status} onChange={handleChange} disabled={isLoading}>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Category Image</Form.Label>
            <div className="mb-2 p-2 rounded border border-danger" style={{ backgroundColor: "#fef2f2" }}>
              <p className="text-danger fw-bold mb-0 small">
                <i className="fa-solid fa-triangle-exclamation me-2"></i>
                IMPORTANT NOTE: Image must be exactly 2200 x 2000 pixels.
              </p>
            </div>
            <Form.Control
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              disabled={isLoading}
            />
            {(imageFile || formData.image_url) && (
              <div className="mt-3 p-2 border rounded d-flex align-items-center gap-3">
                <img
                  src={imageFile ? URL.createObjectURL(imageFile) : formData.image_url}
                  alt="Preview"
                  style={{
                    width: "50px",
                    height: "50px",
                    objectFit: "cover",
                    borderRadius: "4px",
                  }}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
                <div className="flex-grow-1 overflow-hidden">
                  <small className="text-muted d-block text-truncate">
                    {imageFile ? imageFile.name : "Current Image"}
                  </small>
                </div>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={handleRemoveImage}
                  disabled={isLoading}
                >
                  <FaRegTrashAlt />
                </Button>
              </div>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="dark" type="submit" disabled={isLoading}>
            {isLoading ? <Spinner animation="border" size="sm" /> : editId ? "Update" : "Save"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ExamCategoryModal;
